import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { followWriteMutationFn } from '../../api/write/follow';
import { OFFLINE_MUTATION_DEFAULTS } from '../../lib/mutations/offlineMutationOptions';
import { mutationKeys } from '../../lib/mutations/mutationKeys';
import { assertQueueHasCapacity } from '../../lib/mutations/queueBounds';
import { invalidateAfterFollow } from '../../lib/query/invalidateMutations';
import { queryKeys } from '../../lib/query/queryKeys';
import { useCurrentUserProfile } from '../../contexts/CurrentUserProfileContext';

/**
 * Follow / unfollow through the offline queue (Stage 5): with no signal the write waits on the phone and goes through
 * when it is back online, even after the app is closed. Your Following list changes at once either way.
 * follow(person) takes { id, display_name?, profile_photo_url? }; isPaused says the last write is waiting for signal.
 */
export function useFollowWrite() {
  const queryClient = useQueryClient();
  const { session, profile } = useCurrentUserProfile();
  const viewerId = session?.user?.id ?? null;
  const mutationKey = viewerId ? mutationKeys.followWrite(viewerId) : null;

  const m = useMutation({
    mutationKey,
    scope: viewerId ? { id: `follow-${viewerId}` } : undefined,
    ...OFFLINE_MUTATION_DEFAULTS,
    mutationFn: followWriteMutationFn,
    onMutate: async ({ action, targetId, person }) => {
      assertQueueHasCapacity(queryClient);
      const key = queryKeys.people.following(viewerId);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (list = []) => {
        const rest = list.filter((p) => p.id !== targetId);
        return action === 'follow'
          ? [{ id: targetId, display_name: '', ...person }, ...rest]
          : rest;
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx && 'prev' in ctx)
        queryClient.setQueryData(queryKeys.people.following(viewerId), ctx.prev);
    },
    onSuccess: (_data, { targetId }) =>
      invalidateAfterFollow(queryClient, { viewerId, targetUserId: targetId }),
  });

  const write = useCallback(
    (action, person) => {
      if (!viewerId || !person?.id || person.id === viewerId) return;
      const { id, display_name = '', profile_photo_url = null } = person;
      m.mutate({
        action,
        viewerId,
        targetId: id,
        viewerName: profile?.display_name || '',
        person: { id, display_name, profile_photo_url },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [viewerId, profile?.display_name, m.mutate],
  );

  return {
    follow: useCallback((person) => write('follow', person), [write]),
    unfollow: useCallback((person) => write('unfollow', person), [write]),
    isPaused: m.isPaused,
  };
}
